import * as React from "react";
import {
  TreeViewModel,
  TreeViewComponent,
  SidebarComponent,
  NodeClickEventArgs,
  NodeSelectEventArgs,
} from "@syncfusion/ej2-react-navigations";
import {
  MaskedTextBoxComponent,
  MaskedTextBoxModel,
} from "@syncfusion/ej2-react-inputs";
import { Icon } from "@blueprintjs/core";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  navigationTreeViewStore,
  SearchResults,
} from "./stores/NavigationTreeViewStore";
import { useNavigate } from "react-router";
import {
  NavigationTreeViewNode,
  useNavigationTreeViewConfig,
} from "./config/useNavigationTreeViewConfig";
import { UclRouteObject } from "../../../../utils/routes";
import { NavigationExpandModes } from "../types";
import { utils } from "../../../../";
import { useEffect, useRef, useState } from "react";

export default interface AutoHeightSettings {
  autoHeightOffset: number;
}

export interface NavigationTreeViewProps {
  uclRouteObject: UclRouteObject[];
  sideBarRef?: React.MutableRefObject<SidebarComponent | null>;
  navigationExpandMode: NavigationExpandModes;
  showSearchBar?: boolean;
  onReady?: () => void;
  liftUpNavigationRef?: (arg: React.RefObject<TreeViewComponent>) => void;
  useNodeClicked?: boolean;
}
export const NavigationTreeView: React.FC<NavigationTreeViewProps> = (
  props
) => {
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = React.useState<SearchResults>();
  const [maskedTextBoxSettings, setMaskedTextBoxSettings] = React.useState<
    MaskedTextBoxModel & DefaultHtmlAttributes
  >();
  const [treeViewSettings, setTreeViewSettings] = React.useState<
    TreeViewModel & DefaultHtmlAttributes
  >();

  const [treeViewSearchToolbarHeight, setTreeViewSearchToolbarHeight] =
    useState<number>();
  const [navigationTreeViewNode, setNavigationTreeViewNode] = React.useState<
    NavigationTreeViewNode[]
  >([]);

  const treeViewSearchToolbar = useRef<HTMLDivElement>(null);
  const maskedTextBoxRef = useRef<MaskedTextBoxComponent>(null);
  const treeViewItemRef = useRef<TreeViewComponent>(null);

  const { generateNavigationTreeViewConfig } = useNavigationTreeViewConfig();

  useEffect(() => {
    if (props.liftUpNavigationRef && treeViewItemRef) {
      props.liftUpNavigationRef(
        treeViewItemRef as React.RefObject<TreeViewComponent>
      );
    }
  }, [treeViewItemRef, props.liftUpNavigationRef]);

  useEffect(() => {
    var treeViewNode = generateNavigationTreeViewConfig(
      props.uclRouteObject,
      props.navigationExpandMode
    );
    setNavigationTreeViewNode([...treeViewNode]);
  }, []);

  // Setup
  useEffect(() => {
    if (!navigationTreeViewNode) return;
    const [maskedTextBoxModel, treeViewModel] = navigationTreeViewStore.setup(
      navigationTreeViewNode
    );

    setMaskedTextBoxSettings(maskedTextBoxModel);
    setTreeViewSettings(treeViewModel);

    props.onReady?.();

    return () => {
      navigationTreeViewStore.reset();
    };
  }, [navigationTreeViewNode]);

  // Initialize
  useEffect(() => {
    if (treeViewItemRef.current && props.sideBarRef?.current) {
      navigationTreeViewStore.initialize(
        treeViewItemRef.current,
        props.sideBarRef?.current
      );
    }
  }, [treeViewItemRef, props.sideBarRef]);

  useEffect(() => {
    if (maskedTextBoxRef.current) {
      navigationTreeViewStore.initializeSearchBox(maskedTextBoxRef.current);
    }
  }, [maskedTextBoxRef]);

  useEffect(() => {
    if (searchResults?.expand && treeViewItemRef.current) {
      treeViewItemRef.current.expandAll();
    }
  }, [searchResults]);

  const handleMaskedTextBoxChange = () => {
    const results = navigationTreeViewStore.search();
    setSearchResults(results);
  };

  const componentTreeViewToolbarHeightSettings: AutoHeightSettings = {
    autoHeightOffset: 0,
  };

  const initializeAutoHeight = () => {
    // Inner Function
    const autoHeight = () => {
      if (treeViewSearchToolbar.current) {
        setTreeViewSearchToolbarHeight(
          componentTreeViewToolbarHeightSettings.autoHeightOffset
        );
      }
    };

    // Init
    autoHeight();

    // On Resize
    window.addEventListener("resize", () => {
      autoHeight();
    });
  };

  React.useEffect(() => {
    if (!!treeViewSearchToolbar.current) {
      initializeAutoHeight();
    }
  });

  return (
    <div
      className="ucl-navigationtree-toolbar"
      style={{ height: treeViewSearchToolbarHeight }}
      ref={treeViewSearchToolbar}
    >
      {props.showSearchBar && (
        <div className="ucl-navigationtree-input-container">
          <MaskedTextBoxComponent
            className="ucl-navigationtree-input"
            ref={maskedTextBoxRef}
            change={handleMaskedTextBoxChange}
            {...maskedTextBoxSettings}
          />
          <Icon icon={"search"} size={14} />
        </div>
      )}
      <div className="ucl-navigationtree-input-list">
        {treeViewSettings && (
          <TreeViewComponent
            ref={treeViewItemRef}
            nodeExpanding={() => {
              if (
                props.sideBarRef?.current &&
                !props.sideBarRef.current.isOpen
              ) {
                navigationTreeViewStore.open();
              }
            }}
            fullRowNavigable
            nodeSelected={(e: NodeSelectEventArgs) => {
              if (!props.useNodeClicked) {
                const route = utils.routes.findRoute(
                  props.uclRouteObject,
                  e.nodeData.id as string
                );

                if (route) {
                  navigate(
                    `${route.parentRoute?.path || ""}/${route.path}`.replace(
                      "//",
                      "/"
                    )
                  );
                }
              }
            }}
            nodeClicked={(e: NodeClickEventArgs) => {
              if (props.useNodeClicked) {
                const route = utils.routes.findRoute(
                  props.uclRouteObject,
                  e.node.dataset.uid as string
                );

                if (route) {
                  navigate(
                    `${route.parentRoute?.path || ""}/${route.path}`.replace(
                      "//",
                      "/"
                    )
                  );
                }
              }
            }}
            {...treeViewSettings}
          />
        )}
      </div>
    </div>
  );
};
